import { createContext, useContext, type ReactNode } from 'react';
import { useFeatureFlag, COMPUTE_BLOCKS_FLAG } from '~/hooks/queries/use-feature-flags';

/**
 * FRONT-2670: whether this org shows its users compute blocks instead of
 * tokens. Read once at the app root, the same shape as the tooltips
 * preference, so the chat cards that render it stay free of a data
 * dependency and every surface answers the same way.
 *
 * Default `false` everywhere: no provider (a test, a detached render) and a
 * flag that is still loading, failed or missing all mean today's display.
 * Showing spend to a customer's users is that customer's call.
 */
const ComputeBlocksDisplayContext = createContext(false);

export function ComputeBlocksDisplayProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  return (
    <ComputeBlocksDisplayContext.Provider value={enabled}>{children}</ComputeBlocksDisplayContext.Provider>
  );
}

/** Mounted at the app root, inside the query client. */
export function ComputeBlocksFlagProvider({ children }: { children: ReactNode }) {
  const enabled = useFeatureFlag(COMPUTE_BLOCKS_FLAG, false);
  return <ComputeBlocksDisplayProvider enabled={enabled}>{children}</ComputeBlocksDisplayProvider>;
}

export function useShowComputeBlocks(): boolean {
  return useContext(ComputeBlocksDisplayContext);
}
